import { FC, useState } from "react";
import ExcelJS from "exceljs";
import { createDynamicLinksBulk } from "../services/api";
import type {
    BulkCreateDynamicLinkRequest,
    BulkCreateDynamicLinkResponse,
    DynamicLinkDTO,
    PasswordMode,
} from "../types";

interface GenerateDynamicLinksModalProps {
    // Fornire qrCodeIds OPPURE allOfClientId (mutuamente esclusivi).
    qrCodeIds?: number[];
    allOfClientId?: number;
    onClose: () => void;
}

// URL cliccabile in-app: usa l'origine corrente (dev: localhost, prod: qrflow.it).
// NB: l'export (buildRows/CSV/XLSX) continua a usare l.url = dominio di produzione.
const publicUrl = (publicId: string) => `${window.location.origin}/d/${publicId}`;

const inputCls =
    "w-full bg-slate-900/50 border border-slate-700 text-white rounded-xl focus:ring-2 focus:ring-sky-500 block p-3";
const labelCls = "block text-sm font-medium text-slate-300 mb-2";

const GenerateDynamicLinksModal: FC<GenerateDynamicLinksModalProps> = ({
    qrCodeIds,
    allOfClientId,
    onClose,
}) => {
    const isClientMode = allOfClientId != null;
    // --- Opzioni ---
    const [rotationSeconds, setRotationSeconds] = useState<number>(300);
    const [validFrom, setValidFrom] = useState<string>("");
    const [validTo, setValidTo] = useState<string>("");
    const [maxAccesses, setMaxAccesses] = useState<number>(-1);
    const [passwordMode, setPasswordMode] = useState<PasswordMode>("NONE");
    const [sharedPassword, setSharedPassword] = useState<string>("");

    // --- Stato richiesta ---
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<BulkCreateDynamicLinkResponse | null>(null);

    const handleSubmit = async () => {
        if (!isClientMode && (!qrCodeIds || qrCodeIds.length === 0)) {
            setError("Nessun QR selezionato.");
            return;
        }
        if (passwordMode === "SHARED" && !sharedPassword.trim()) {
            setError("Inserisci la password condivisa.");
            return;
        }

        setIsSubmitting(true);
        setError(null);

        const req: BulkCreateDynamicLinkRequest = {
            ...(isClientMode ? { allOfClientId } : { qrCodeIds }),
            rotationSeconds,
            validFrom: validFrom ? new Date(validFrom).toISOString() : null,
            validTo: validTo ? new Date(validTo).toISOString() : null,
            maxAccesses: maxAccesses === -1 ? null : maxAccesses,
            passwordMode,
            sharedPassword: passwordMode === "SHARED" ? sharedPassword : null,
        };

        try {
            const response = await createDynamicLinksBulk(req);
            setResult(response.data.data);
        } catch (err: any) {
            setError(
                err?.response?.data?.message ||
                    err?.message ||
                    "Errore durante la generazione dei link dinamici.",
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    // Righe per l'export (la password in chiaro è disponibile SOLO qui, nella response).
    const buildRows = (created: DynamicLinkDTO[]) =>
        created.map((l) => ({
            descrizione: l.label || l.code || "",
            code: l.code ?? "",
            url: l.url,
            password: l.password ?? "",
            scadenza: l.validTo ?? "",
        }));

    const triggerDownload = (blob: Blob, filename: string) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const handleDownloadCsv = () => {
        if (!result) return;
        const rows = buildRows(result.created);
        const header = ["Descrizione", "Code QR", "URL pubblico", "Password", "Scadenza"];
        const escape = (v: string) => `"${String(v).replace(/"/g, '""')}"`;
        const lines = [
            header.map(escape).join(","),
            ...rows.map((r) =>
                [r.descrizione, r.code, r.url, r.password, r.scadenza].map(escape).join(","),
            ),
        ];
        // BOM per compatibilità Excel con caratteri accentati
        const blob = new Blob(["﻿" + lines.join("\r\n")], {
            type: "text/csv;charset=utf-8;",
        });
        triggerDownload(blob, `link_dinamici_${Date.now()}.csv`);
    };

    const handleDownloadXlsx = async () => {
        if (!result) return;
        const rows = buildRows(result.created);
        const workbook = new ExcelJS.Workbook();
        const ws = workbook.addWorksheet("Link Dinamici");
        ws.columns = [
            { header: "Descrizione", key: "descrizione", width: 30 },
            { header: "Code QR", key: "code", width: 28 },
            { header: "URL pubblico", key: "url", width: 45 },
            { header: "Password", key: "password", width: 18 },
            { header: "Scadenza", key: "scadenza", width: 22 },
        ];
        ws.getRow(1).font = { bold: true };
        rows.forEach((r) => ws.addRow(r));
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        triggerDownload(blob, `link_dinamici_${Date.now()}.xlsx`);
    };

    const hasPasswords = result?.created.some((l) => l.password);

    return (
        <div
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex justify-center items-center p-4 animate-fade-in"
            onClick={onClose}
        >
            <div
                className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-6 border-b border-slate-700 bg-slate-900/50 rounded-t-2xl flex justify-between items-start">
                    <div>
                        <h3 className="text-xl font-bold text-white">Genera link dinamici</h3>
                        <p className="text-slate-400 text-sm mt-1">
                            {result
                                ? "Generazione completata. Scarica subito l'elenco (le password sono mostrate una sola volta)."
                                : isClientMode
                                  ? "Verrà creato un link dinamico con QR rotante per tutti i QR del cliente selezionato."
                                  : `Un link dinamico con QR rotante verrà creato per ${qrCodeIds?.length ?? 0} QR selezionati.`}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-700 transition-colors"
                    >
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                </div>

                <div className="p-6 overflow-y-auto flex-1 bg-slate-900/30 space-y-5">
                    {error && (
                        <div className="p-4 bg-red-900/20 border border-red-800/50 text-red-200 rounded-xl">
                            {error}
                        </div>
                    )}

                    {!result ? (
                        <>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className={labelCls}>Rotazione QR (secondi)</label>
                                    <input
                                        type="number"
                                        min={10}
                                        value={rotationSeconds}
                                        onChange={(e) => setRotationSeconds(Number(e.target.value))}
                                        className={inputCls}
                                    />
                                    <p className="text-xs text-slate-500 mt-1">Default 300 (5 minuti).</p>
                                </div>
                                <div>
                                    <label className={labelCls}>Accessi massimi</label>
                                    <input
                                        type="number"
                                        min={-1}
                                        value={maxAccesses}
                                        onChange={(e) => setMaxAccesses(Number(e.target.value))}
                                        className={inputCls}
                                    />
                                    <p className="text-xs text-slate-500 mt-1">-1 per illimitati.</p>
                                </div>
                                <div>
                                    <label className={labelCls}>Valido dal</label>
                                    <input
                                        type="datetime-local"
                                        value={validFrom}
                                        onChange={(e) => setValidFrom(e.target.value)}
                                        className={inputCls}
                                    />
                                </div>
                                <div>
                                    <label className={labelCls}>Valido fino al</label>
                                    <input
                                        type="datetime-local"
                                        value={validTo}
                                        onChange={(e) => setValidTo(e.target.value)}
                                        className={inputCls}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className={labelCls}>Protezione con password</label>
                                <select
                                    value={passwordMode}
                                    onChange={(e) => setPasswordMode(e.target.value as PasswordMode)}
                                    className={inputCls}
                                >
                                    <option value="NONE">Nessuna</option>
                                    <option value="SHARED">Password fissa condivisa</option>
                                    <option value="PER_PERSON">Password diversa per persona</option>
                                </select>
                            </div>

                            {passwordMode === "SHARED" && (
                                <div className="animate-fade-in">
                                    <label className={labelCls}>Password condivisa *</label>
                                    <input
                                        type="text"
                                        value={sharedPassword}
                                        onChange={(e) => setSharedPassword(e.target.value)}
                                        className={inputCls}
                                        placeholder="Es. Evento2026"
                                    />
                                </div>
                            )}

                            {passwordMode === "PER_PERSON" && (
                                <div className="p-4 bg-amber-900/20 border border-amber-800/40 text-amber-200 rounded-xl text-sm">
                                    Verrà generata una password casuale per ogni link. Le password sono
                                    mostrate <strong>solo una volta</strong> in questa schermata: scarica
                                    l'elenco subito dopo la generazione.
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="p-4 bg-slate-800 border border-slate-700 rounded-xl text-center">
                                    <p className="text-xs text-slate-400 uppercase font-semibold">Creati</p>
                                    <p className="text-2xl font-bold text-emerald-400 mt-1">
                                        {result.created.length}
                                    </p>
                                </div>
                                <div className="p-4 bg-slate-800 border border-slate-700 rounded-xl text-center">
                                    <p className="text-xs text-slate-400 uppercase font-semibold">Saltati</p>
                                    <p className="text-2xl font-bold text-slate-300 mt-1">
                                        {result.skipped.length}
                                    </p>
                                </div>
                            </div>

                            {result.skipped.length > 0 && (
                                <div className="p-4 bg-slate-800 border border-slate-700 rounded-xl">
                                    <p className="text-sm font-semibold text-slate-300 mb-2">QR saltati</p>
                                    <ul className="space-y-1 text-sm text-slate-400">
                                        {result.skipped.map((s, i) => (
                                            <li key={i}>
                                                QR #{s.qrCodeId}: {s.reason}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            <div className="border border-slate-700 rounded-xl overflow-hidden">
                                <table className="w-full text-left text-sm">
                                    <thead className="bg-slate-800 text-xs uppercase text-slate-400">
                                        <tr>
                                            <th className="p-3">Descrizione</th>
                                            <th className="p-3">URL</th>
                                            {hasPasswords && <th className="p-3">Password</th>}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-700">
                                        {result.created.map((l) => (
                                            <tr key={l.id} className="bg-slate-900/40">
                                                <td className="p-3 text-white">{l.label || l.code}</td>
                                                <td className="p-3 text-sky-400 font-mono break-all">
                                                    <a
                                                        href={publicUrl(l.publicId)}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="hover:text-sky-300"
                                                    >
                                                        {publicUrl(l.publicId)}
                                                    </a>
                                                </td>
                                                {hasPasswords && (
                                                    <td className="p-3 font-mono text-amber-300">
                                                        {l.password ?? "-"}
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>

                <div className="p-6 border-t border-slate-700 bg-slate-800 rounded-b-2xl flex justify-end gap-3">
                    {!result ? (
                        <>
                            <button
                                onClick={onClose}
                                className="px-5 py-2.5 text-slate-300 font-medium hover:text-white transition-colors"
                            >
                                Annulla
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={isSubmitting}
                                className="px-5 py-2.5 bg-gradient-to-r from-sky-600 to-indigo-600 hover:from-sky-500 hover:to-indigo-500 text-white font-bold rounded-xl disabled:opacity-50 shadow-lg shadow-sky-500/20 transition-all"
                            >
                                {isSubmitting ? "Generazione..." : "Genera link"}
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                onClick={onClose}
                                className="px-5 py-2.5 text-slate-300 font-medium hover:text-white transition-colors"
                            >
                                Chiudi
                            </button>
                            <button
                                onClick={handleDownloadCsv}
                                className="px-5 py-2.5 bg-slate-700 hover:bg-slate-600 text-white font-bold rounded-xl transition-all"
                            >
                                Scarica CSV
                            </button>
                            <button
                                onClick={handleDownloadXlsx}
                                className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl shadow-lg shadow-emerald-500/20 transition-all"
                            >
                                Scarica XLSX
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default GenerateDynamicLinksModal;
